<script setup lang="ts">
import { useElementBounding } from "@vueuse/core";
import { SimpleTooltip } from "ls/components/simple-tooltip";
import { computed, ref, useTemplateRef } from "vue";
import { round } from "@/common/lib/number";

type State
  = {
    type: "idle";
  } | {
    type: "resizing";
    currentWidth?: number;
    currentAspectRatio?: number;
    startedWidth: number;
    startedHeight: number;
    startedRatio: number;
    startedX?: number;
    startedY?: number;
  };

const props = defineProps<{
  allowAspectRatioChange?: boolean;
}>();

const emits = defineEmits<{
  pointerUp: [void];
  pointerDown: [void];
}>();

const width = defineModel<number>("width");
const aspectRatio = defineModel<number>("aspectRatio");

const state = ref<State>({ type: "idle" });

const minWidth = 25; // px
const minHeight = 25; // px
const container = useTemplateRef("container");
const content = useTemplateRef("content");
const { width: containerWidth } = useElementBounding(container);
const { width: contentWidth, height: contentHeight } = useElementBounding(content);

function getAllowedWidth(val: number): number {
  if (val > containerWidth.value) {
    return containerWidth.value;
  } else if (val < minWidth) {
    return minWidth;
  }
  return val;
}

function getAllowedHeight(val: number): number {
  if (val < minHeight) {
    return minHeight;
  }
  return val;
}

function getWidthPercentage(val: number): number {
  return (val / containerWidth.value) * 100;
}

function setWidth(val: number, saveAspectRatio: boolean) {
  if (state.value.type !== "resizing") return;

  const newWidth = getAllowedWidth(val);
  updateWidth(getWidthPercentage(newWidth));

  if (!props.allowAspectRatioChange) return;
  if (saveAspectRatio) {
    const newHeight = newWidth / state.value.startedRatio;
    if (newHeight !== getAllowedHeight(newHeight)) return;
    updateAspectRatio(state.value.startedRatio);
  } else {
    updateAspectRatio(newWidth / state.value.startedHeight);
  }
}

function setHeight(val: number, saveAspectRatio: boolean) {
  if (state.value.type !== "resizing") return;

  const newHeight = getAllowedHeight(val);
  if (saveAspectRatio) {
    const newWidth = newHeight * state.value.startedRatio;
    if (newWidth !== getAllowedWidth(newWidth)) return;
    updateAspectRatio(state.value.startedRatio);
    updateWidth(getWidthPercentage(newWidth));
  } else {
    updateAspectRatio(contentWidth.value / newHeight);
    updateWidth(getWidthPercentage(state.value.startedWidth));
  }
}

function updateWidth(percent: number) {
  if (state.value.type !== "resizing") return;
  state.value.currentWidth = round(percent, 2);
}

function updateAspectRatio(ratio: number) {
  if (state.value.type !== "resizing") return;
  state.value.currentAspectRatio = round(ratio, 3);
}

function setPointerCapture(event: PointerEvent) {
  if (event.target instanceof HTMLElement) {
    event.target.setPointerCapture(event.pointerId);
  }
}

function handleWidthPointerDown(event: PointerEvent) {
  handlePointerDown(event, true);
}

function handleHeightPointerDown(event: PointerEvent) {
  handlePointerDown(event, false);
}

function handlePointerDown(event: PointerEvent, isWidth: boolean) {
  setPointerCapture(event);
  emits("pointerDown");

  state.value = {
    type: "resizing",
    currentWidth: width.value,
    currentAspectRatio: aspectRatio.value,
    startedWidth: contentWidth.value,
    startedHeight: contentHeight.value,
    startedRatio: contentWidth.value / contentHeight.value,
    ...(isWidth ? { startedX: event.clientX } : { startedY: event.clientY }),
  };
}

function handlePointerMove(e: PointerEvent) {
  if (state.value.type !== "resizing") return;

  if (state.value.startedX !== undefined) {
    const deltaX = e.clientX - state.value.startedX;
    const newWidth = state.value.startedWidth + Math.round(deltaX);
    // Save aspect ratio only if not allowed to change or Shift key is pressed
    const saveAspectRatio = !props.allowAspectRatioChange || e.shiftKey;
    setWidth(newWidth, saveAspectRatio);
  } else if (state.value.startedY !== undefined) {
    const deltaY = e.clientY - state.value.startedY;
    const newHeight = state.value.startedHeight + Math.round(deltaY);
    // Save aspect ratio only if Shift key is pressed
    setHeight(newHeight, e.shiftKey);
  }
}

function handlePointerUp(_: PointerEvent) {
  emits("pointerUp");
  if (state.value.type !== "resizing") return;

  width.value = state.value.currentWidth;
  if (props.allowAspectRatioChange) {
    aspectRatio.value = state.value.currentAspectRatio;
  }
  state.value = { type: "idle" };
}

const styleWidth = computed(() => {
  let value = width.value;
  if (state.value.type === "resizing") {
    value = state.value.currentWidth;
  }
  return value && `${value}%`;
});

const styleAspectRatio = computed(() => {
  if (state.value.type === "resizing") {
    return state.value.currentAspectRatio;
  }
  return aspectRatio.value;
});
</script>

<template>
  <div ref="container" class="group relative w-full select-none">
    <div
      ref="content"
      class="max-w-full"
      :style="{ width: styleWidth, aspectRatio: styleAspectRatio }"
    >
      <slot />
    </div>
    <div
      class="absolute top-0 flex h-full w-3 justify-end overflow-hidden"
      :style="{ left: `${contentWidth}px` }"
    >
      <SimpleTooltip content="Drag to resize" side="bottom">
        <div
          class="my-auto h-16 w-1 cursor-ew-resize touch-none rounded-full bg-[#3EACEF] opacity-0 group-hover:opacity-100 in-[.ProseMirror-selectednode]:opacity-100"
          @pointerdown="handleWidthPointerDown"
          @pointermove="handlePointerMove"
          @pointerup="handlePointerUp"
        />
      </SimpleTooltip>
    </div>
    <div
      v-if="allowAspectRatioChange"
      class="absolute left-0 flex h-3 justify-end overflow-hidden"
      :style="{ top: `${contentHeight}px`, width: `${contentWidth}px` }"
    >
      <SimpleTooltip content="Drag to resize" side="bottom">
        <div
          class="mx-auto h-1 w-16 cursor-row-resize touch-none rounded-full bg-[#3EACEF] group-hover:opacity-100 in-[.ProseMirror-selectednode]:opacity-100"
          @pointerdown="handleHeightPointerDown"
          @pointermove="handlePointerMove"
          @pointerup="handlePointerUp"
        />
      </SimpleTooltip>
    </div>
  </div>
</template>
